import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MigrationManifest, PersistedState } from 'redux-persist';
import { type RootState } from '@/store';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { ChatHistoryData } from '@/api';

interface ChatHistoryListState {
  allChatHistoryList: ChatHistoryData[];
  chatHistoryList: ChatHistoryData[];
  chatHistoryListRowCount: number;
  chatHistoryListPagination: PaginationState;
  chatHistoryListSorting: SortingState;
  chatHistoryListFilters: {
    userIds: string[];
    search?: string;
    caseReferences: string[];
  };
}

const CHAT_HISTORY_SORTABLE_COLUMNS = ['latestActivity', 'createdAt'];

export const DEFAULT_CHAT_HISTORY_SORTING: SortingState = [{ id: 'latestActivity', desc: true }];

const initialState: ChatHistoryListState = {
  allChatHistoryList: [],
  chatHistoryList: [],
  chatHistoryListRowCount: 0,
  chatHistoryListPagination: {
    pageIndex: 0,
    pageSize: 50,
  },
  chatHistoryListSorting: DEFAULT_CHAT_HISTORY_SORTING,
  chatHistoryListFilters: {
    userIds: [],
    search: '',
    caseReferences: [],
  },
};

const chatHistoryListSlice = createSlice({
  name: 'chatHistory',
  initialState: initialState,
  reducers: {
    setAllChatHistoryList: (state, action: PayloadAction<ChatHistoryData[]>) => {
      state.allChatHistoryList = action.payload;
      // When server-side pagination is used, this is the current page only.
      state.chatHistoryList = action.payload;
    },
    setChatHistoryRowCount: (state, action: PayloadAction<number>) => {
      state.chatHistoryListRowCount = action.payload;
    },
    setChatHistoryPagination: (state, action: PayloadAction<PaginationState>) => {
      state.chatHistoryListPagination = action.payload;
    },
    setChatHistorySorting: (state, action: PayloadAction<SortingState>) => {
      state.chatHistoryListSorting = action.payload;
    },
    setChatHistoryFilters: (
      state,
      action: PayloadAction<{ userIds?: string[]; search?: string; caseReferences?: string[] }>,
    ) => {
      if (action.payload.userIds !== undefined) {
        state.chatHistoryListFilters.userIds = action.payload.userIds;
      }
      if (action.payload.search !== undefined) {
        state.chatHistoryListFilters.search = action.payload.search;
      }
      if (action.payload.caseReferences !== undefined) {
        state.chatHistoryListFilters.caseReferences = action.payload.caseReferences;
      }
    },
    updateChatHistoryTitle: (state, action: PayloadAction<{ chatId: string; title: string }>) => {
      const { chatId, title } = action.payload;

      const update = (list: ChatHistoryData[]) => {
        const idx = list.findIndex((c) => c.chatId === chatId);
        if (idx !== -1) {
          list[idx] = { ...list[idx], title };
        }
      };

      update(state.allChatHistoryList);
      update(state.chatHistoryList);
    },
  },
});

export const {
  setAllChatHistoryList,
  setChatHistoryRowCount,
  setChatHistoryPagination,
  setChatHistorySorting,
  setChatHistoryFilters,
  updateChatHistoryTitle,
} = chatHistoryListSlice.actions;

export const selectAllChatHistoryList = (state: RootState) => {
  return state.chatHistory.allChatHistoryList;
};

export const selectChatHistoryList = (state: RootState) => {
  return state.chatHistory.chatHistoryList;
};

export const selectChatHistoryRowCount = (state: RootState) => {
  return state.chatHistory.chatHistoryListRowCount;
};

export const selectChatHistoryPagination = (state: RootState) => {
  return state.chatHistory.chatHistoryListPagination;
};

export const selectChatHistorySorting = (state: RootState) => {
  return state.chatHistory.chatHistoryListSorting;
};

export const selectChatHistoryFilters = (state: RootState) => {
  return state.chatHistory.chatHistoryListFilters;
};

export const chatHistoryListReducer = chatHistoryListSlice.reducer;

export const CHAT_HISTORY_PERSIST_VERSION = 1;

export const chatHistoryPersistMigrations: MigrationManifest = {
  // Older sessions saved a sort on `lastUpdated`, which isn't a History column.
  1: (state) => {
    const persisted = state as PersistedState & Partial<ChatHistoryListState>;
    const sortId = persisted.chatHistoryListSorting?.[0]?.id ?? '';
    if (CHAT_HISTORY_SORTABLE_COLUMNS.includes(sortId)) return state;

    return { ...persisted, chatHistoryListSorting: DEFAULT_CHAT_HISTORY_SORTING };
  },
};
